import React, { useState } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Button,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Videocam as VideocamIcon,
  Headset as HeadsetIcon,
  Notes as NotesIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import stockImg from "../../assets/notfound.jpg";
import Badge from "@mui/material/Badge";


export default function TalentCard({
  profile,
  brokenImageIds,
  handleFavouriteClick,
  handleMessageClick,
  showListDropdown = false,
  currentListName,
  onListChange,
  onNoteClick,
  disableMessageButton,
  isSubmissionView = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState([]);

  const hasImage = !brokenImageIds.includes(profile.userId);

  const getDisplayImage = () => {
    if (isSubmissionView && profile.imageURL) {
      return profile.imageURL;
    }
    if (isSubmissionView && profile.media) {
      const ext = profile.media.split(".").pop()?.toLowerCase();
      if (["jpg", "jpeg", "png", "webp"].includes(ext)) {
        return profile.media;
      }
    }
    return hasImage ? profile.talentImage : stockImg;
  };

  const buttonStyle = (isSelected) => ({
    padding: "4px 8px",
    minWidth: 30,
    color: isSelected ? "#eee!important" : "#202020!important",
    borderColor: isSelected ? "#202020!important" : "#eee!important",
    backgroundColor: isSelected ? "#202020!important" : "transparent!important",
    boxShadow: "none!important",
    "&:hover": {
      backgroundColor: isSelected ? "#2f2f2f!important" : "#f8f8f8!important",
      borderColor: isSelected ? "#202020!important" : "#eee!important",
      boxShadow: "none!important",
    },
  });

  const handleMediaClick = () => {
    setDialogType("media");
    setDialogTitle("Media Files");
    setDialogContent(
      profile.media ? [{ name: "Media File", url: profile.media }] : [],
    );
    setDialogOpen(true);
  };

  const handleCoverLettersClick = () => {
    setDialogType("coverLetters");
    setDialogTitle("Cover Letters");
    setDialogContent(
      profile.coverLetters
        ? [{ name: "Cover Letter", url: profile.coverLetters }]
        : [],
    );
    setDialogOpen(true);
  };

  const handleAdditionalLinkClick = () => {
    if (!profile.additionalLinks) return;
    let url = profile.additionalLinks.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogType("");
    setDialogTitle("");
    setDialogContent([]);
  };

  const hasAnyActions =
    (profile.media && profile.media.trim() !== "") ||
    (profile.coverLetters && profile.coverLetters.trim() !== "") ||
    (profile.additionalLinks && profile.additionalLinks.trim() !== "") ||
    onNoteClick ||
    !disableMessageButton ||
    showListDropdown;

  return (
    <>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%", // fill grid cell so all cards are equal height
        }}
      >
        {/* Favorite toggle */}
        <IconButton
          onClick={(e) =>
            handleFavouriteClick(e, profile.userId, profile.favorite)
          }
          size="small"
          disableRipple
          sx={{
            position: "absolute",
            padding: "20px",
            right: 0,
            top: 0,
            height: "50px",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "transparent" },
            zIndex: 2,
          }}
        >
          {profile.favorite ? (
            <FavoriteIcon fontSize="medium" color="error" />
          ) : (
            <FavoriteBorderIcon fontSize="medium" color="disabled" />
          )}
        </IconButton>

        {/* Image — fixed height, cropped to top (face-anchored) */}
        <Box
          sx={{
            width: "100%",
            // Responsive height: shorter on mobile, taller on desktop
            height: { xs: 300, sm: 340, md: 380 },
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <CardMedia
            component="img"
            image={getDisplayImage()}
            alt={`${profile.firstName} ${profile.lastName}`}
            onError={(e) => (e.target.src = stockImg)}
            loading="lazy"
            onClick={() =>
              window.open(
                `/talent/${profile.userId}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top", // anchor to face, not middle
              cursor: "pointer",
            }}
          />
        </Box>

        {/* Union badge */}
        <Typography
          variant="body2"
          sx={{
            backgroundColor: "#202020",
            color: "#eee",
            padding: "4px 5px",
            textAlign: "center",
            fontSize: { xs: "0.7rem", sm: "0.8rem" },
            flexShrink: 0,
          }}
        >
          {profile.union || "Non Union"}
        </Typography>

        {/* Name + icons */}
        <CardContent
          sx={{
            pt: 1,
            px: 1,
            pb: 1,
            textAlign: "center",
            flexGrow: 1, // push actions to bottom
          }}
        >
          <Typography
            sx={{
              cursor: "pointer",
              fontWeight: 400,
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.2rem" },
              lineHeight: 1.3,
              mb: 0.5,
            }}
            onClick={() =>
              window.open(
                `/talent/${profile.userId}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
            gutterBottom
          >
            {profile.firstName} {profile.lastName}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ minHeight: "20px" }}
          >
            {profile.hasVideo && (
              <VideocamIcon fontSize="small" sx={{ color: "black" }} />
            )}
            {profile.hasAudio && (
              <HeadsetIcon fontSize="small" sx={{ color: "black" }} />
            )}
          </Stack>
        </CardContent>

        {/* Actions */}
        {hasAnyActions && (
          <CardActions
            sx={{
              justifyContent: showListDropdown ? "space-between" : "center",
              pb: 2,
              px: 2,
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              {profile.media && profile.media.trim() !== "" && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMediaClick}
                  startIcon={<AttachFileIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    border: "none",
                    minWidth: 0,
                    padding: 0,
                  }}
                />
              )}

              {profile.coverLetters && profile.coverLetters.trim() !== "" && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCoverLettersClick}
                  startIcon={<DescriptionIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    border: "none",
                    minWidth: 0,
                    padding: 0,
                  }}
                />
              )}

              {profile.additionalLinks && profile.additionalLinks.trim() !== "" && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAdditionalLinkClick}
                  startIcon={<LinkIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    border: "none",
                    minWidth: 0,
                    padding: 0,
                  }}
                />
              )}

              {onNoteClick && (
                <IconButton
                  onClick={() => onNoteClick(profile)}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#eee" },
                    zIndex: 2,
                  }}
                >
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!profile.note || !profile.note.trim()}
                    overlap="circular"
                  >
                    <NotesIcon fontSize="small" />
                  </Badge>
                </IconButton>
              )}

              {!disableMessageButton && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleMessageClick(profile)}
                  sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                >
                  Message
                </Button>
              )}
            </Box>

            {showListDropdown && (
              <Stack direction="row" spacing={1}>
                {["A", "B", "C", "X"].map((list) => (
                  <Button
                    key={list}
                    variant={currentListName === list ? "contained" : "outlined"}
                    size="small"
                    disabled={currentListName === list}
                    onClick={() => onListChange(profile.userId, list)}
                    sx={buttonStyle(currentListName === list)}
                  >
                    {list}
                  </Button>
                ))}
              </Stack>
            )}
          </CardActions>
        )}
      </Card>

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{dialogTitle}</Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {dialogType === "media" && profile.media ? (
            <Box sx={{ textAlign: "center", p: 2 }}>
              {(() => {
                const mediaUrl = profile.media;
                const extension = mediaUrl.split(".").pop()?.toLowerCase();

                if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
                  return (
                    <img
                      src={mediaUrl}
                      alt="Media content"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  );
                } else if (["mp4", "mov", "avi", "webm"].includes(extension)) {
                  return (
                    <video
                      controls
                      style={{
                        maxWidth: "100%",
                        maxHeight: "400px",
                        borderRadius: "8px",
                      }}
                    >
                      <source src={mediaUrl} />
                      Your browser does not support the video tag.
                    </video>
                  );
                } else if (["mp3", "wav", "ogg"].includes(extension)) {
                  return (
                    <audio controls style={{ width: "100%", marginTop: "20px" }}>
                      <source src={mediaUrl} />
                      Your browser does not support the audio tag.
                    </audio>
                  );
                } else {
                  return (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        Media file available
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<ViewIcon />}
                        onClick={() => window.open(mediaUrl, "_blank")}
                      >
                        Open Media
                      </Button>
                    </Box>
                  );
                }
              })()}
            </Box>
          ) : dialogType === "coverLetters" && profile.coverLetters ? (
            <Box sx={{ p: 2 }}>
              <div
                dangerouslySetInnerHTML={{ __html: profile.coverLetters }}
                style={{
                  lineHeight: "1.6",
                  fontSize: "14px",
                  wordWrap: "break-word",
                }}
              />
            </Box>
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No {dialogTitle.toLowerCase()} available
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          {dialogType === "media" && profile.media && (
            <Button
              onClick={() => window.open(profile.media, "_blank")}
              color="primary"
              startIcon={<ViewIcon />}
            >
              Open in New Tab
            </Button>
          )}
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}